<script lang="ts">
    import { onMount } from "svelte";
    import { apiClient } from "./apiClient";
    import {
        drinks,
        categories,
        tags,
        loading,
        error,
        success,
    } from "./stores";
    import DrinkForm from "./DrinkForm.svelte";
    import type { Drink } from "./apiClient";

    let showForm = false;
    let editingDrink: Drink | null = null;
    let editingRowId: number | null = null;

    onMount(async () => {
        await loadData();
    });

    async function loadData() {
        try {
            loading.set(true);
            error.set(null);

            const [drinksData, categoriesData, tagsData] = await Promise.all([
                apiClient.getDrinks(),
                apiClient.getCategories(),
                apiClient.getTags(),
            ]);

            drinks.set(drinksData);
            categories.set(categoriesData);
            tags.set(tagsData);
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to load data",
            );
        } finally {
            loading.set(false);
        }
    }

    async function deleteDrink(drink: Drink) {
        if (!confirm(`Are you sure you want to delete "${drink.name}"?`)) {
            return;
        }

        try {
            loading.set(true);
            error.set(null);

            await apiClient.deleteDrink(drink.id);
            await loadData();
            success.set("Drink deleted successfully");
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to delete drink",
            );
        } finally {
            loading.set(false);
        }
    }

    function handleAddDrink() {
        editingDrink = null;
        editingRowId = -1; // Special ID for new drink
        showForm = true;
    }

    function handleEditDrink(drink: Drink) {
        editingDrink = drink;
        editingRowId = drink.id;
        showForm = true;
    }

    function handleFormCancel() {
        showForm = false;
        editingDrink = null;
        editingRowId = null;
    }

    async function handleFormSave() {
        showForm = false;
        editingDrink = null;
        editingRowId = null;
        await loadData();
    }
</script>

<div class="section">
    <h2>Drinks Management</h2>

    <button class="btn btn-primary" on:click={handleAddDrink}>
        Add New Drink
    </button>

    {#if showForm && editingRowId === -1}
        <div class="inline-form">
            <DrinkForm
                drink={editingDrink}
                isEditing={editingDrink !== null}
                onCancel={handleFormCancel}
                onSave={handleFormSave}
            />
        </div>
    {/if}

    {#if $loading}
        <div class="loading">Loading...</div>
    {/if}

    {#if $drinks.length > 0}
        <table class="table">
            <thead>
                <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Categories</th>
                    <th>Tags</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {#each $drinks as drink}
                    <tr>
                        <td data-label="ID">{drink.id}</td>
                        <td data-label="Name">{drink.name}</td>
                        <td data-label="Categories">
                            {#if drink.categories && drink.categories.length > 0}
                                {#each drink.categories as category}
                                    <span class="tag">{category.name}</span>
                                {/each}
                            {:else}
                                <span class="text-muted">None</span>
                            {/if}
                        </td>
                        <td data-label="Tags">
                            {#if drink.tags && drink.tags.length > 0}
                                {#each drink.tags as tag}
                                    <span class="tag">{tag.name}</span>
                                {/each}
                            {:else}
                                <span class="text-muted">None</span>
                            {/if}
                        </td>
                        <td data-label="Actions" class="actions">
                            <button
                                class="btn btn-secondary"
                                on:click={() => handleEditDrink(drink)}
                                disabled={$loading}
                            >
                                Edit
                            </button>
                            <button
                                class="btn btn-danger"
                                on:click={() => deleteDrink(drink)}
                                disabled={$loading}
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                    {#if showForm && editingRowId === drink.id}
                        <tr class="inline-form-row">
                            <td colspan="5">
                                <div class="inline-form">
                                    <DrinkForm
                                        drink={editingDrink}
                                        isEditing={editingDrink !== null}
                                        onCancel={handleFormCancel}
                                        onSave={handleFormSave}
                                    />
                                </div>
                            </td>
                        </tr>
                    {/if}
                {/each}
            </tbody>
        </table>
    {:else if !$loading}
        <p>No drinks found. Add your first drink!</p>
    {/if}
</div>

<style>
    .inline-form-row {
        background-color: #f8f9fa;
    }

    .inline-form-row td {
        padding: 0 !important;
    }

    .inline-form {
        padding: 20px;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        background-color: #fff;
        margin: 10px 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
</style>
